import { createContext, useContext } from 'react';

/**
 * WHICH TAGS ARE IN USE, for every tag chip on the page.
 *
 * A tag in the lens should look the same wherever it shows - in a table, on a
 * card, in the drawer - so the reader can see at a glance which rows it
 * matched. The page sets this once; `TagChip` reads it. Keys are
 * "DIMENSION:value", as the lens stores them.
 *
 * - `lead`: tags the followed lead holds - dark blue, and not a button, since
 *   only Back on the lead bar takes them out.
 * - `lens`: tags added to the lens past the lead - light blue.
 */
export interface LensTags {
  lead: ReadonlySet<string>;
  lens: ReadonlySet<string>;
}

export const LensTagsContext = createContext<LensTags>({ lead: new Set(), lens: new Set() });

export type LensState = 'lead' | 'lens' | null;

/** What part of the lens one tag is in. Pure. */
export function lensState(tags: LensTags, key: string): LensState {
  if (tags.lead.has(key)) return 'lead';
  if (tags.lens.has(key)) return 'lens';
  return null;
}

/** The view's tags, split into the lead's and the ones added past it. Pure. */
export function splitLensTags(viewTags: string[], leadTags: string[] = []): LensTags {
  const lead = new Set(leadTags);
  return { lead, lens: new Set(viewTags.filter(t => !lead.has(t))) };
}

export function useLensState(dimension: string, value: string): LensState {
  return lensState(useContext(LensTagsContext), `${dimension}:${value}`);
}
